import api from "./api";
import type {
  Room,
  RoomType,
  RoomPackage,
  FrontDeskBooking,
  GuestProfile,
  Offer,
  AvailabilityCalendarRow,
} from "../types";

export const adminBookingService = {
  getAllBookings: async (limit: number = 50, offset: number = 0) => {
    try {
      return await api.get("/bookings", { limit, offset });
    } catch (error) {
      console.error("Error fetching bookings:", error);
      throw error;
    }
  },

  markNoShow: async (id: string) => {
    try {
      return await api.post(`/bookings/${id}/no-show`);
    } catch (error) {
      console.error("Error marking booking no-show:", error);
      throw error;
    }
  },

  getBookingsByStatus: async (status: string) => {
    try {
      return await api.get("/bookings", { status });
    } catch (error) {
      console.error("Error fetching bookings:", error);
      throw error;
    }
  },

  getBooking: async (id: string) => {
    try {
      return await api.get(`/bookings/${id}`);
    } catch (error) {
      console.error("Error fetching booking:", error);
      throw error;
    }
  },

  updateBooking: async (id: string, data: any) => {
    try {
      return await api.put(`/bookings/${id}`, data);
    } catch (error) {
      console.error("Error updating booking:", error);
      throw error;
    }
  },

  updateBookingV2: async (id: string, data: any) => {
    try {
      const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/v1$/, '/v2') + `/bookings/${id}` : `http://localhost:5000/api/v2/bookings/${id}`;
      return await api.put(url, data);
    } catch (error) {
      console.error("Error updating v2 booking:", error);
      throw error;
    }
  },

  updateBulkBookingV2: async (id: string, data: any) => {
    try {
      const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/v1$/, '/v2') + `/bookings/bulk/${id}` : `http://localhost:5000/api/v2/bookings/bulk/${id}`;
      return await api.put(url, data);
    } catch (error) {
      console.error("Error updating v2 bulk booking:", error);
      throw error;
    }
  },

  confirmBooking: async (id: string, roomId?: string) => {
    try {
      return await api.post(`/bookings/${id}/confirm`, { roomId });
    } catch (error) {
      console.error("Error confirming booking:", error);
      throw error;
    }
  },

  allocateRooms: async (id: string, data: { roomIds: string[], note?: string }) => {
    try {
      return await api.post(`/bookings/${id}/allocate`, data);
    } catch (error) {
      console.error("Error allocating rooms:", error);
      throw error;
    }
  },

  cancelBooking: async (id: string) => {
    try {
      return await api.post(`/bookings/${id}/cancel`);
    } catch (error) {
      console.error("Error cancelling booking:", error);
      throw error;
    }
  },

  deleteBooking: async (id: string) => {
    try {
      return await api.delete(`/bookings/${id}`);
    } catch (error) {
      console.error("Error deleting booking:", error);
      throw error;
    }
  },

  deleteBookingChild: async (id: string, childIndex: number) => {
    try {
      return await api.delete(`/bookings/${id}/child/${childIndex}`);
    } catch (error) {
      console.error("Error deleting booking child:", error);
      throw error;
    }
  },

  getBookingStats: async () => {
    try {
      return await api.get("/bookings/stats/overview");
    } catch (error) {
      console.error("Error fetching booking stats:", error);
      throw error;
    }
  },

  getBookingFines: async (bookingId: string) => {
    try {
      const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/v1$/, '/v2') + `/bookings/${bookingId}/fines` : `http://localhost:5000/api/v2/bookings/${bookingId}/fines`;
      return await api.get(url);
    } catch (error) {
      console.error("Error fetching booking fines:", error);
      throw error;
    }
  },

  saveBookingFines: async (bookingId: string, data: any) => {
    try {
      const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/v1$/, '/v2') + `/bookings/${bookingId}/fines` : `http://localhost:5000/api/v2/bookings/${bookingId}/fines`;
      return await api.put(url, data);
    } catch (error) {
      console.error("Error saving booking fines:", error);
      throw error;
    }
  },

  getInspectionForm: async (bookingId: string) => {
    try {
      const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/v1$/, '/v2') + `/bookings/${bookingId}/inspection-form` : `http://localhost:5000/api/v2/bookings/${bookingId}/inspection-form`;
      return await api.getBlob(url);
    } catch (error) {
      console.error("Error generating inspection form:", error);
      throw error;
    }
  },
};


export const adminRoomService = {
  getAllRoomTypes: async () => {
    try {
      return await api.get<RoomType[]>("/rooms/types");
    } catch (error) {
      console.error("Error fetching room types:", error);
      throw error;
    }
  },

  getRoomType: async (id: string) => {
    try {
      return await api.get(`/rooms/types/${id}`);
    } catch (error) {
      console.error("Error fetching room type:", error);
      throw error;
    }
  },

  createRoomType: async (data: any) => {
    try {
      return await api.post("/rooms/types", data);
    } catch (error) {
      console.error("Error creating room type:", error);
      throw error;
    }
  },

  updateRoomType: async (id: string, data: any) => {
    try {
      return await api.put(`/rooms/types/${id}`, data);
    } catch (error) {
      console.error("Error updating room type:", error);
      throw error;
    }
  },

  deleteRoomType: async (id: string) => {
    try {
      return await api.delete(`/rooms/types/${id}`);
    } catch (error) {
      console.error("Error deleting room type:", error);
      throw error;
    }
  },

  getRoomTypeAvailability: async (
    id: string,
    params: { checkInDate: string; checkOutDate: string },
  ) => {
    try {
      return await api.get<{ isAvailable: boolean; availableCount: number }>(
        `/rooms/types/${id}/availability`,
        params,
      );
    } catch (error) {
      console.error("Error fetching room type availability:", error);
      throw error;
    }
  },

  replaceRoomImages: async (id: string, images: any[]) => {
    try {
      return await api.put(`/rooms/types/${id}/images`, { images });
    } catch (error) {
      console.error("Error replacing room images:", error);
      throw error;
    }
  },

  createRoomImage: async (id: string, data: any) => {
    try {
      return await api.post(`/rooms/types/${id}/images`, data);
    } catch (error) {
      console.error("Error creating room image:", error);
      throw error;
    }
  },

  uploadRoomImage: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      return await api.upload("/rooms/images/upload", formData);
    } catch (error) {
      console.error("Error uploading room image:", error);
      throw error;
    }
  },

  uploadRoomMedia: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("media", file);
      return await api.upload("/rooms/media/upload", formData);
    } catch (error) {
      console.error("Error uploading room media:", error);
      throw error;
    }
  },

  updateRoomImage: async (roomTypeId: string, imageId: string, data: any) => {
    try {
      return await api.put(
        `/rooms/types/${roomTypeId}/images/${imageId}`,
        data,
      );
    } catch (error) {
      console.error("Error updating room image:", error);
      throw error;
    }
  },

  deleteRoomImage: async (roomTypeId: string, imageId: string) => {
    try {
      return await api.delete(`/rooms/types/${roomTypeId}/images/${imageId}`);
    } catch (error) {
      console.error("Error deleting room image:", error);
      throw error;
    }
  },

  getAllRooms: async () => {
    try {
      return await api.get<Room[]>("/rooms");
    } catch (error) {
      console.error("Error fetching rooms:", error);
      throw error;
    }
  },

  getRoom: async (id: string) => {
    try {
      return await api.get(`/rooms/${id}`);
    } catch (error) {
      console.error("Error fetching room:", error);
      throw error;
    }
  },

  createRoom: async (data: any) => {
    try {
      return await api.post("/rooms", data);
    } catch (error) {
      console.error("Error creating room:", error);
      throw error;
    }
  },

  updateRoom: async (id: string, data: any) => {
    try {
      return await api.put(`/rooms/${id}`, data);
    } catch (error) {
      console.error("Error updating room:", error);
      throw error;
    }
  },

  deleteRoom: async (id: string) => {
    try {
      return await api.delete(`/rooms/${id}`);
    } catch (error) {
      console.error("Error deleting room:", error);
      throw error;
    }
  },
};

export const adminRestaurantReservationService = {
  getAll: async (
    limit: number = 50,
    offset: number = 0,
    status?: string,
    search?: string,
  ) => {
    try {
      return await api.get("/restaurant-reservations", {
        limit,
        offset,
        status,
        search,
      });
    } catch (error) {
      console.error("Error fetching restaurant reservations:", error);
      throw error;
    }
  },

  update: async (id: string, data: any) => {
    try {
      return await api.put(`/restaurant-reservations/${id}`, data);
    } catch (error) {
      console.error("Error updating restaurant reservation:", error);
      throw error;
    }
  },

  confirm: async (id: string) => {
    try {
      return await api.post(`/restaurant-reservations/${id}/confirm`);
    } catch (error) {
      console.error("Error confirming restaurant reservation:", error);
      throw error;
    }
  },

  cancel: async (id: string) => {
    try {
      return await api.post(`/restaurant-reservations/${id}/cancel`);
    } catch (error) {
      console.error("Error cancelling restaurant reservation:", error);
      throw error;
    }
  },
};

export const adminSportsReservationService = {
  getAll: async (
    limit: number = 50,
    offset: number = 0,
    status?: string,
    search?: string,
    activityTitle?: string,
  ) => {
    try {
      return await api.get("/sports-activity-reservations", {
        limit,
        offset,
        status,
        search,
        activityTitle,
      });
    } catch (error) {
      console.error("Error fetching sports activity reservations:", error);
      throw error;
    }
  },

  update: async (id: string, data: any) => {
    try {
      return await api.put(`/sports-activity-reservations/${id}`, data);
    } catch (error) {
      console.error("Error updating sports activity reservation:", error);
      throw error;
    }
  },

  confirm: async (id: string) => {
    try {
      return await api.post(`/sports-activity-reservations/${id}/confirm`);
    } catch (error) {
      console.error("Error confirming sports activity reservation:", error);
      throw error;
    }
  },

  cancel: async (id: string) => {
    try {
      return await api.post(`/sports-activity-reservations/${id}/cancel`);
    } catch (error) {
      console.error("Error cancelling sports activity reservation:", error);
      throw error;
    }
  },
};

export const adminContactSubmissionService = {
  getAll: async (
    limit: number = 50,
    offset: number = 0,
    status?: string,
    search?: string,
  ) => {
    try {
      return await api.get("/contact-submissions", {
        limit,
        offset,
        status,
        search,
      });
    } catch (error) {
      console.error("Error fetching contact submissions:", error);
      throw error;
    }
  },

  update: async (id: string, data: any) => {
    try {
      return await api.put(`/contact-submissions/${id}`, data);
    } catch (error) {
      console.error("Error updating contact submission:", error);
      throw error;
    }
  },

  markInProgress: async (id: string) => {
    try {
      return await api.post(`/contact-submissions/${id}/mark-in-progress`);
    } catch (error) {
      console.error("Error marking contact submission in progress:", error);
      throw error;
    }
  },

  resolve: async (id: string) => {
    try {
      return await api.post(`/contact-submissions/${id}/resolve`);
    } catch (error) {
      console.error("Error resolving contact submission:", error);
      throw error;
    }
  },
};

export const frontDeskService = {
  getDashboard: async () => {
    try {
      return await api.get("/front-desk/dashboard");
    } catch (error) {
      console.error("Error fetching front desk dashboard:", error);
      throw error;
    }
  },

  createBooking: async (data: any) => {
    try {
      return await api.post("/front-desk/bookings", data);
    } catch (error) {
      console.error("Error creating front desk booking:", error);
      throw error;
    }
  },

  createBookingV2: async (data: any) => {
    try {
      const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/v1$/, '/v2') + "/front-desk/bookings" : "http://localhost:5000/api/v2/front-desk/bookings";
      return await api.post(url, data);
    } catch (error) {
      console.error("Error creating v2 front desk booking:", error);
      throw error;
    }
  },

  createBulkBookings: async (data: any) => {
    try {
      return await api.post("/front-desk/bulk-bookings", data);
    } catch (error) {
      console.error("Error creating bulk front desk bookings:", error);
      throw error;
    }
  },

  createBulkBookingsV2: async (data: any) => {
    try {
      const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/v1$/, '/v2') + "/front-desk/bulk-bookings" : "http://localhost:5000/api/v2/front-desk/bulk-bookings";
      return await api.post(url, data);
    } catch (error) {
      console.error("Error creating v2 bulk front desk bookings:", error);
      throw error;
    }
  },

  getRoomPackages: async () => {
    try {
      const url = process.env.REACT_APP_API_URL
        ? process.env.REACT_APP_API_URL.replace(/\/v1$/, '/v2') + "/front-desk/room-packages"
        : "http://localhost:5000/api/v2/front-desk/room-packages";
      return await api.get<RoomPackage[]>(url);
    } catch (error) {
      console.error("Error fetching room packages:", error);
      throw error;
    }
  },

  generateQuotation: async (data: any) => {
    try {
      return await api.post("/front-desk/quotation", data, { responseType: 'blob' });
    } catch (error) {
      console.error("Error generating quotation:", error);
      throw error;
    }
  },

  getBookings: async (params?: {
    status?: string;
    reservation_source?: string;
    search?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }) => {
    try {
      return await api.get("/front-desk/bookings", params);
    } catch (error) {
      console.error("Error fetching front desk bookings:", error);
      throw error;
    }
  },

  getWalkInBookings: async (params?: {
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) => {
    try {
      return await api.get<FrontDeskBooking[]>("/front-desk/walk-in-bookings", params);
    } catch (error) {
      console.error("Error fetching walk-in bookings:", error);
      throw error;
    }
  },

  allocateRoom: async (
    bookingId: string,
    data?: { room_id?: string; note?: string; force?: boolean; force_reason?: string },
  ) => {
    try {
      return await api.post(
        `/front-desk/bookings/${bookingId}/allocate-room`,
        data || {},
      );
    } catch (error) {
      console.error("Error allocating room:", error);
      throw error;
    }
  },

  checkIn: async (
    bookingId: string,
    data?: {
      early_check_in?: boolean;
      early_check_in_fee?: number;
      receipt_type?: string;
      vat_rate?: number;
      pay_remaining?: boolean;
      payment_method?: string;
      transaction_id?: string;
      room_package_id?: string | null;
      id_document_type?: string;
      id_document_number?: string;
      id_document_url?: string;
    },
  ) => {
    try {
      return await api.post(
        `/front-desk/bookings/${bookingId}/check-in`,
        data || {},
      );
    } catch (error) {
      console.error("Error checking in booking:", error);
      throw error;
    }
  },

  checkOut: async (
    bookingId: string,
    data?: {
      late_check_out?: boolean;
      late_check_out_fee?: number;
      payment_method?: string;
      transaction_id?: string;
      receipt_type?: "vat" | "vat_free";
      vat_rate?: number;
      settle_balance?: boolean;
      allow_outstanding_balance?: boolean;
      outstanding_reason?: string;
    },
  ) => {
    try {
      return await api.post(
        `/front-desk/bookings/${bookingId}/check-out`,
        data || {},
      );
    } catch (error) {
      console.error("Error checking out booking:", error);
      throw error;
    }
  },

  getInvoiceBreakdown: async (bookingId: string, basis?: "contract" | "accrual") => {
    try {
      return await api.get(
        `/front-desk/bookings/${bookingId}/invoice-breakdown${
          basis ? `?basis=${basis}` : ""
        }`,
      );
    } catch (error) {
      console.error("Error fetching invoice breakdown:", error);
      throw error;
    }
  },

  extendStay: async (
    bookingId: string,
    newCheckOutDate: string,
    force?: boolean,
    forceReason?: string,
  ) => {
    try {
      return await api.post(
        `/front-desk/bookings/${bookingId}/extend-stay`,
        { new_check_out_date: newCheckOutDate, force, force_reason: forceReason },
      );
    } catch (error) {
      console.error("Error extending stay:", error);
      throw error;
    }
  },

  changeRoom: async (
    bookingId: string,
    roomId: string,
    reason?: string,
    force?: boolean,
    forceReason?: string,
  ) => {
    try {
      return await api.post(
        `/front-desk/bookings/${bookingId}/change-room`,
        { room_id: roomId, reason, force, force_reason: forceReason },
      );
    } catch (error) {
      console.error("Error changing room:", error);
      throw error;
    }
  },

  getRoomChangeLog: async (bookingId: string) => {
    try {
      return await api.get(`/front-desk/bookings/${bookingId}/room-change-log`);
    } catch (error) {
      console.error("Error getting room change log:", error);
      throw error;
    }
  },

  getAvailabilityCalendar: async (params: {
    startDate: string;
    endDate: string;
    roomTypeId?: string;
  }) => {
    try {
      return await api.get<import("../types").AvailabilityCalendarRow[]>("/front-desk/availability-calendar", params);
    } catch (error) {
      console.error("Error fetching availability calendar:", error);
      throw error;
    }
  },

  getGuestProfiles: async (params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }) => {
    try {
      return await api.get<GuestProfile[]>("/front-desk/guest-profiles", params);
    } catch (error) {
      console.error("Error fetching guest profiles:", error);
      throw error;
    }
  },

  getGuestProfileById: async (id: string): Promise<any> => {
    try {
      const response = await api.get(`/front-desk/guest-profiles/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching guest profile by ID:", error);
      throw error;
    }
  },

  updateGuestProfile: async (guestProfileId: string, data: any) => {
    try {
      return await api.put(
        `/front-desk/guest-profiles/${guestProfileId}`,
        data,
      );
    } catch (error) {
      console.error("Error updating guest profile:", error);
      throw error;
    }
  },

  deleteGuestProfile: async (guestProfileId: string) => {
    try {
      return await api.delete(`/front-desk/guest-profiles/${guestProfileId}`);
    } catch (error) {
      console.error("Error deleting guest profile:", error);
      throw error;
    }
  },

  updateBooking: async (bookingId: string, data: any) => {
    try {
      return await api.put(`/front-desk/bookings/${bookingId}`, data);
    } catch (error) {
      console.error("Error updating booking:", error);
      throw error;
    }
  },

  updateBookingV2: async (bookingId: string, data: any) => {
    try {
      const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/v1$/, '/v2') + `/front-desk/bookings/${bookingId}` : `http://localhost:5000/api/v2/front-desk/bookings/${bookingId}`;
      return await api.put(url, data);
    } catch (error) {
      console.error("Error updating v2 booking:", error);
      throw error;
    }
  },

  updateGroupBooking: async (groupId: string, data: any) => {
    try {
      return await api.put(`/front-desk/bookings/group/${groupId}`, data);
    } catch (error) {
      console.error("Error updating group booking:", error);
      throw error;
    }
  },

  updateBulkBookingV2: async (groupId: string, data: any) => {
    try {
      const url = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/v1$/, '/v2') + `/front-desk/bulk-bookings/${groupId}` : `http://localhost:5000/api/v2/front-desk/bulk-bookings/${groupId}`;
      return await api.put(url, data);
    } catch (error) {
      console.error("Error updating v2 bulk booking:", error);
      throw error;
    }
  },

  deleteBooking: async (bookingId: string) => {
    try {
      return await api.delete(`/front-desk/bookings/${bookingId}`);
    } catch (error) {
      console.error("Error deleting booking:", error);
      throw error;
    }
  },

  getBookingFines: async (bookingId: string) => {
    try {
      return await api.get(`/front-desk/bookings/${bookingId}/fines`);
    } catch (error) {
      console.error("Error fetching booking fines:", error);
      throw error;
    }
  },

  saveBookingFines: async (bookingId: string, data: any) => {
    try {
      return await api.put(`/front-desk/bookings/${bookingId}/fines`, data);
    } catch (error) {
      console.error("Error saving booking fines:", error);
      throw error;
    }
  },

  getInspectionForm: async (bookingId: string) => {
    try {
      return await api.getBlob(`/front-desk/bookings/${bookingId}/inspection-form`);
    } catch (error) {
      console.error("Error downloading inspection form:", error);
      throw error;
    }
  },

  uploadIdDocument: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("document", file);
      return await api.upload("/front-desk/upload-id-document", formData);
    } catch (error) {
      console.error("Error uploading ID document:", error);
      throw error;
    }
  },
};

export const offersService = {
  getOffers: async () => {
    try {
      return await api.get<Offer[]>("/offers");
    } catch (error) {
      console.error("Error fetching offers:", error);
      throw error;
    }
  },
};

export const adminRoomWorkflowService = {
  getHousekeepingTasks: async (params?: {
    room_id?: string;
    status?: string;
    assigned_to?: string;
    limit?: number;
    offset?: number;
  }) => {
    try {
      return await api.get("/rooms/housekeeping/tasks", params);
    } catch (error) {
      console.error("Error fetching housekeeping tasks:", error);
      throw error;
    }
  },

  createHousekeepingTask: async (data: any) => {
    try {
      return await api.post("/rooms/housekeeping/tasks", data);
    } catch (error) {
      console.error("Error creating housekeeping task:", error);
      throw error;
    }
  },

  updateHousekeepingTaskStatus: async (
    id: string,
    status: string,
    note?: string,
  ) => {
    try {
      return await api.post(`/rooms/housekeeping/tasks/${id}/status`, {
        status,
        note,
      });
    } catch (error) {
      console.error("Error updating housekeeping task status:", error);
      throw error;
    }
  },

  assignHousekeepingTask: async (id: string, assignedTo: string | null) => {
    try {
      return await api.post(`/rooms/housekeeping/tasks/${id}/assign`, {
        assigned_to: assignedTo,
      });
    } catch (error) {
      console.error("Error assigning housekeeping task:", error);
      throw error;
    }
  },

  getMaintenanceIssues: async (params?: {
    room_id?: string;
    status?: string;
    priority?: string;
    assigned_to?: string;
    limit?: number;
    offset?: number;
  }) => {
    try {
      return await api.get("/rooms/maintenance/issues", params);
    } catch (error) {
      console.error("Error fetching maintenance issues:", error);
      throw error;
    }
  },

  createMaintenanceIssue: async (data: any) => {
    try {
      return await api.post("/rooms/maintenance/issues", data);
    } catch (error) {
      console.error("Error creating maintenance issue:", error);
      throw error;
    }
  },

  updateMaintenanceIssue: async (id: string, data: any) => {
    try {
      return await api.put(`/rooms/maintenance/issues/${id}`, data);
    } catch (error) {
      console.error("Error updating maintenance issue:", error);
      throw error;
    }
  },
};

export const adminRestaurantPosService = {
  getDiningTables: async () => {
    try {
      return await api.get("/restaurant-pos/tables");
    } catch (error) {
      console.error("Error fetching dining tables:", error);
      throw error;
    }
  },

  createDiningTable: async (data: any) => {
    try {
      return await api.post("/restaurant-pos/tables", data);
    } catch (error) {
      console.error("Error creating dining table:", error);
      throw error;
    }
  },

  updateDiningTable: async (id: string, data: any) => {
    try {
      return await api.put(`/restaurant-pos/tables/${id}`, data);
    } catch (error) {
      console.error("Error updating dining table:", error);
      throw error;
    }
  },

  deleteDiningTable: async (id: string) => {
    try {
      return await api.delete(`/restaurant-pos/tables/${id}`);
    } catch (error) {
      console.error("Error deleting dining table:", error);
      throw error;
    }
  },

  getMenuCategories: async () => {
    try {
      return await api.get("/restaurant-pos/menu/categories");
    } catch (error) {
      console.error("Error fetching menu categories:", error);
      throw error;
    }
  },

  createMenuCategory: async (data: any) => {
    try {
      return await api.post("/restaurant-pos/menu/categories", data);
    } catch (error) {
      console.error("Error creating menu category:", error);
      throw error;
    }
  },

  updateMenuCategory: async (id: string, data: any) => {
    try {
      return await api.put(`/restaurant-pos/menu/categories/${id}`, data);
    } catch (error) {
      console.error("Error updating menu category:", error);
      throw error;
    }
  },

  deleteMenuCategory: async (id: string) => {
    try {
      return await api.delete(`/restaurant-pos/menu/categories/${id}`);
    } catch (error) {
      console.error("Error deleting menu category:", error);
      throw error;
    }
  },

  getMenuItems: async (categoryId?: string) => {
    try {
      return await api.get(
        "/restaurant-pos/menu/items",
        categoryId ? { categoryId } : undefined,
      );
    } catch (error) {
      console.error("Error fetching menu items:", error);
      throw error;
    }
  },

  createMenuItem: async (data: any) => {
    try {
      return await api.post("/restaurant-pos/menu/items", data);
    } catch (error) {
      console.error("Error creating menu item:", error);
      throw error;
    }
  },

  uploadMenuImage: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      return await api.upload("/restaurant-pos/menu/images/upload", formData);
    } catch (error) {
      console.error("Error uploading menu image:", error);
      throw error;
    }
  },

  updateMenuItem: async (id: string, data: any) => {
    try {
      return await api.put(`/restaurant-pos/menu/items/${id}`, data);
    } catch (error) {
      console.error("Error updating menu item:", error);
      throw error;
    }
  },

  deleteMenuItem: async (id: string) => {
    try {
      return await api.delete(`/restaurant-pos/menu/items/${id}`);
    } catch (error) {
      console.error("Error deleting menu item:", error);
      throw error;
    }
  },

  getRestaurantPageContent: async () => {
    try {
      return await api.get("/restaurant-pos/content");
    } catch (error) {
      console.error("Error fetching restaurant page content:", error);
      throw error;
    }
  },

  updateRestaurantPageContent: async (data: any) => {
    try {
      return await api.put("/restaurant-pos/content", data);
    } catch (error) {
      console.error("Error updating restaurant page content:", error);
      throw error;
    }
  },

  getOrders: async (params?: {
    status?: string;
    order_type?: string;
    table_id?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) => {
    try {
      return await api.get("/restaurant-pos/orders", params);
    } catch (error) {
      console.error("Error fetching POS orders:", error);
      throw error;
    }
  },

  getOrderById: async (id: string) => {
    try {
      return await api.get(`/restaurant-pos/orders/${id}`);
    } catch (error) {
      console.error("Error fetching POS order:", error);
      throw error;
    }
  },

  deleteOrder: async (id: string) => {
    try {
      return await api.delete(`/restaurant-pos/orders/${id}`);
    } catch (error) {
      console.error("Error deleting POS order:", error);
      throw error;
    }
  },

  createOrder: async (data: any) => {
    try {
      return await api.post("/restaurant-pos/orders", data);
    } catch (error) {
      console.error("Error creating POS order:", error);
      throw error;
    }
  },

  updateOrder: async (id: string, data: any) => {
    try {
      return await api.put(`/restaurant-pos/orders/${id}`, data);
    } catch (error) {
      console.error("Error updating POS order:", error);
      throw error;
    }
  },

  updateOrderStatus: async (id: string, status: string, payment_method?: string) => {
    try {
      return await api.post(`/restaurant-pos/orders/${id}/status`, { status, payment_method });
    } catch (error) {
      console.error("Error updating order status:", error);
      throw error;
    }
  },

  getKotQueue: async () => {
    try {
      return await api.get("/restaurant-pos/kot/queue");
    } catch (error) {
      console.error("Error fetching KOT queue:", error);
      throw error;
    }
  },

  updateKotItemStatus: async (itemId: string, status: string) => {
    try {
      return await api.post(`/restaurant-pos/kot/items/${itemId}/status`, {
        status,
      });
    } catch (error) {
      console.error("Error updating KOT item status:", error);
      throw error;
    }
  },

  createSplitBill: async (orderId: string, data: any) => {
    try {
      return await api.post(
        `/restaurant-pos/orders/${orderId}/split-bills`,
        data,
      );
    } catch (error) {
      console.error("Error creating split bill:", error);
      throw error;
    }
  },

  settleSplitBill: async (splitBillId: string) => {
    try {
      return await api.post(
        `/restaurant-pos/split-bills/${splitBillId}/settle`,
      );
    } catch (error) {
      console.error("Error settling split bill:", error);
      throw error;
    }
  },

  settleOrderPayment: async (orderId: string, payment_method: string) => {
    try {
      return await api.post(
        `/restaurant-pos/orders/${orderId}/settle-payment`,
        { payment_method },
      );
    } catch (error) {
      console.error("Error settling order payment:", error);
      throw error;
    }
  },

  // Kitchen Printer Groups
  getKitchenGroups: async () => {
    try {
      return await api.get<any>("/restaurant-pos/kitchen-groups");
    } catch (error) {
      console.error("Error fetching kitchen groups:", error);
      throw error;
    }
  },

  createKitchenGroup: async (data: any) => {
    try {
      return await api.post<any>("/restaurant-pos/kitchen-groups", data);
    } catch (error) {
      console.error("Error creating kitchen group:", error);
      throw error;
    }
  },

  updateKitchenGroup: async (id: string, data: any) => {
    try {
      return await api.put<any>(`/restaurant-pos/kitchen-groups/${id}`, data);
    } catch (error) {
      console.error("Error updating kitchen group:", error);
      throw error;
    }
  },

  deleteKitchenGroup: async (id: string) => {
    try {
      return await api.delete<any>(`/restaurant-pos/kitchen-groups/${id}`);
    } catch (error) {
      console.error("Error deleting kitchen group:", error);
      throw error;
    }
  },

  // Customer management for tax invoices
  searchCustomers: async (q: string) => {
    try {
      return await api.get<any>("/restaurant-pos/customers/search", { q });
    } catch (error) {
      console.error("Error searching customers:", error);
      throw error;
    }
  },

  createCustomer: async (data: {
    full_name: string;
    company_name?: string;
    phone?: string;
    address_line_1?: string;
    address_line_2?: string;
    tin_vat_number?: string;
    email?: string;
  }) => {
    try {
      return await api.post<any>("/restaurant-pos/customers", data);
    } catch (error) {
      console.error("Error creating customer:", error);
      throw error;
    }
  },

  updateCustomer: async (id: string, data: {
    full_name?: string;
    company_name?: string;
    phone?: string;
    address_line_1?: string;
    address_line_2?: string;
    tin_vat_number?: string;
    email?: string;
  }) => {
    try {
      return await api.put<any>(`/restaurant-pos/customers/${id}`, data);
    } catch (error) {
      console.error("Error updating customer:", error);
      throw error;
    }
  },

  getBookingGuest: async (bookingId: string) => {
    try {
      return await api.get<any>(`/restaurant-pos/booking-guest/${bookingId}`);
    } catch (error) {
      console.error("Error fetching booking guest:", error);
      throw error;
    }
  },

  getTaxInvoicePdf: async (orderId: string, customerDetails: {
    customerName: string;
    companyName?: string;
    phone?: string;
    address?: string;
    tinVatNumber?: string;
  }): Promise<Blob> => {
    const apiBase = process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1";
    const token = localStorage.getItem("admin_token");
    const response = await fetch(
      `${apiBase}/operations/billing/receipt/order/${orderId}/tax-invoice`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(customerDetails),
      }
    );
    if (!response.ok) throw new Error("Failed to generate tax invoice");
    return await response.blob();
  },
};

export const adminOfferService = {
  uploadOfferImage: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      return await api.upload("/offers/images/upload", formData);
    } catch (error) {
      console.error("Error uploading offer image:", error);
      throw error;
    }
  },
};

export const adminOperationsService = {
  getDashboardInsights: async (params?: {
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      return await api.get("/operations/dashboard/insights", params);
    } catch (error) {
      console.error("Error fetching dashboard insights:", error);
      throw error;
    }
  },

  getBillingInvoices: async (params?: { status?: string; search?: string }) => {
    try {
      return await api.get("/operations/billing/invoices", params);
    } catch (error) {
      console.error("Error fetching billing invoices:", error);
      throw error;
    }
  },

  createBillingInvoice: async (data: any) => {
    try {
      return await api.post("/operations/billing/invoices", data);
    } catch (error) {
      console.error("Error creating billing invoice:", error);
      throw error;
    }
  },

  getBillingOverview: async () => {
    try {
      return await api.get("/operations/billing/overview");
    } catch (error) {
      console.error("Error fetching billing overview:", error);
      throw error;
    }
  },

  recordPayment: async (data: any) => {
    try {
      return await api.post("/operations/billing/payments", data);
    } catch (error) {
      console.error("Error recording payment:", error);
      throw error;
    }
  },

  getBillingReferences: async () => {
    try {
      return await api.get("/operations/billing/search-references");
    } catch (error) {
      console.error("Error fetching billing references:", error);
      throw error;
    }
  },

  settleUnified: async (data: any) => {
    try {
      return await api.post("/operations/billing/settle-unified", data);
    } catch (error) {
      console.error("Error settling unified payment:", error);
      throw error;
    }
  },

  searchFolios: async () => {
    try {
      return await api.get("/operations/billing/search-folios");
    } catch (error) {
      console.error("Error searching folios:", error);
      throw error;
    }
  },

  settleFolio: async (data: { folio_id: string; amount: number; payment_method: string; notes?: string }) => {
    try {
      return await api.post("/operations/billing/settle-folio", data);
    } catch (error) {
      console.error("Error settling folio payment:", error);
      throw error;
    }
  },

  getCrmGuests: async (params?: { search?: string }) => {
    try {
      return await api.get("/operations/crm/guests", params);
    } catch (error) {
      console.error("Error fetching CRM guests:", error);
      throw error;
    }
  },

  updateCrmGuest: async (id: string, data: any) => {
    try {
      return await api.put(`/operations/crm/guests/${id}`, data);
    } catch (error) {
      console.error("Error updating CRM guest:", error);
      throw error;
    }
  },

  getStaffRoles: async () => {
    try {
      return await api.get("/operations/staff/roles");
    } catch (error) {
      console.error("Error fetching staff roles:", error);
      throw error;
    }
  },

  createStaffRole: async (data: any) => {
    try {
      return await api.post("/operations/staff/roles", data);
    } catch (error) {
      console.error("Error creating staff role:", error);
      throw error;
    }
  },

  getStaffMembers: async () => {
    try {
      return await api.get("/operations/staff/members");
    } catch (error) {
      console.error("Error fetching staff members:", error);
      throw error;
    }
  },

  createStaffMember: async (data: any) => {
    try {
      return await api.post("/operations/staff/members", data);
    } catch (error) {
      console.error("Error creating staff member:", error);
      throw error;
    }
  },

  updateStaffMember: async (id: string, data: any) => {
    try {
      return await api.put(`/operations/staff/members/${id}`, data);
    } catch (error) {
      console.error("Error updating staff member:", error);
      throw error;
    }
  },

  getSportsCatalog: async () => {
    try {
      return await api.get("/operations/sports/catalog");
    } catch (error) {
      console.error("Error fetching sports catalog:", error);
      throw error;
    }
  },

  createSportsCatalog: async (data: any) => {
    try {
      return await api.post("/operations/sports/catalog", data);
    } catch (error) {
      console.error("Error creating sports catalog:", error);
      throw error;
    }
  },

  updateSportsCatalog: async (id: string, data: any) => {
    try {
      return await api.put(`/operations/sports/catalog/${id}`, data);
    } catch (error) {
      console.error("Error updating sports catalog:", error);
      throw error;
    }
  },

  deleteSportsCatalog: async (id: string) => {
    try {
      return await api.delete(`/operations/sports/catalog/${id}`);
    } catch (error) {
      console.error("Error deleting sports catalog:", error);
      throw error;
    }
  },

  uploadSportsCatalogImage: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      return await api.upload(
        "/operations/sports/catalog/images/upload",
        formData,
      );
    } catch (error) {
      console.error("Error uploading sports catalog image:", error);
      throw error;
    }
  },

  getSportsSchedules: async (params?: { activityId?: string }) => {
    try {
      return await api.get("/operations/sports/schedules", params);
    } catch (error) {
      console.error("Error fetching sports schedules:", error);
      throw error;
    }
  },

  createSportsSchedule: async (data: any) => {
    try {
      return await api.post("/operations/sports/schedules", data);
    } catch (error) {
      console.error("Error creating sports schedule:", error);
      throw error;
    }
  },

  getSportsReservations: async (params?: { status?: string }) => {
    try {
      return await api.get("/operations/sports/reservations", params);
    } catch (error) {
      console.error("Error fetching sports reservations:", error);
      throw error;
    }
  },

  getUnifiedCalendar: async (params: {
    startDate: string;
    endDate: string;
  }) => {
    try {
      return await api.get("/operations/calendar/unified", params);
    } catch (error) {
      console.error("Error fetching unified calendar:", error);
      throw error;
    }
  },

  getNotifications: async (params?: { status?: string }) => {
    try {
      return await api.get("/operations/notifications", params);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      throw error;
    }
  },

  createNotification: async (data: any) => {
    try {
      return await api.post("/operations/notifications", data);
    } catch (error) {
      console.error("Error creating notification:", error);
      throw error;
    }
  },

  createPaymentIntent: async (data: any) => {
    try {
      return await api.post("/operations/payments/create-intent", data);
    } catch (error) {
      console.error("Error creating payment intent:", error);
      throw error;
    }
  },

  processWebhook: async (data: any) => {
    try {
      return await api.post("/operations/payments/webhook", data);
    } catch (error) {
      console.error("Error processing payment webhook:", error);
      throw error;
    }
  },

  getPaymentEvents: async () => {
    try {
      return await api.get("/operations/payments/events");
    } catch (error) {
      console.error("Error fetching payment events:", error);
      throw error;
    }
  },
};

export const adminWebsiteContentService = {
  getModule: async (moduleKey: string) => {
    try {
      return await api.get(`/website-content/admin/${moduleKey}`);
    } catch (error) {
      console.error("Error fetching website content module:", error);
      throw error;
    }
  },

  updateModule: async (
    moduleKey: string,
    data: { title?: string; content: any; is_published?: boolean },
  ) => {
    try {
      return await api.put(`/website-content/admin/${moduleKey}`, data);
    } catch (error) {
      console.error("Error updating website content module:", error);
      throw error;
    }
  },

  uploadImage: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      return await api.upload("/website-content/admin/images/upload", formData);
    } catch (error) {
      console.error("Error uploading website content image:", error);
      throw error;
    }
  },
};

export const adminReportsService = {
  getMonthlyRevenue: async () => {
    try {
      return await api.get("/operations/reports/monthly-revenue");
    } catch (error) {
      console.error("Error fetching monthly revenue:", error);
      throw error;
    }
  },

  getBookingsByRoomType: async () => {
    try {
      return await api.get("/operations/reports/bookings-by-room-type");
    } catch (error) {
      console.error("Error fetching bookings by room type:", error);
      throw error;
    }
  },

  getTopGuests: async () => {
    try {
      return await api.get("/operations/reports/top-guests");
    } catch (error) {
      console.error("Error fetching top guests:", error);
      throw error;
    }
  },
};

export const adminLaundryService = {
  getOrders: async (params?: {
    status?: string;
    booking_id?: string;
    service_type?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) => {
    try {
      return await api.get("/laundry/orders", params);
    } catch (error) {
      console.error("Error fetching laundry orders:", error);
      throw error;
    }
  },

  getOrderById: async (id: string) => {
    try {
      return await api.get(`/laundry/orders/${id}`);
    } catch (error) {
      console.error("Error fetching laundry order:", error);
      throw error;
    }
  },

  getOrdersByBooking: async (bookingId: string) => {
    try {
      return await api.get(`/laundry/orders/by-booking/${bookingId}`);
    } catch (error) {
      console.error("Error fetching laundry orders for booking:", error);
      throw error;
    }
  },

  createOrder: async (data: any) => {
    try {
      return await api.post("/laundry/orders", data);
    } catch (error) {
      console.error("Error creating laundry order:", error);
      throw error;
    }
  },

  updateOrder: async (id: string, data: any) => {
    try {
      return await api.put(`/laundry/orders/${id}`, data);
    } catch (error) {
      console.error("Error updating laundry order:", error);
      throw error;
    }
  },

  updateOrderStatus: async (id: string, status: string) => {
    try {
      return await api.post(`/laundry/orders/${id}/status`, { status });
    } catch (error) {
      console.error("Error updating laundry order status:", error);
      throw error;
    }
  },

  deleteOrder: async (id: string) => {
    try {
      return await api.delete(`/laundry/orders/${id}`);
    } catch (error) {
      console.error("Error deleting laundry order:", error);
      throw error;
    }
  },
};

const adminServices = {
  adminBookingService,
  adminRoomService,
  adminRestaurantReservationService,
  adminSportsReservationService,
  adminContactSubmissionService,
  frontDeskService,
  adminRoomWorkflowService,
  adminRestaurantPosService,
  adminOperationsService,
  adminWebsiteContentService,
  adminLaundryService,
};

export const adminFolioService = {
  getByBooking: async (bookingId: string) => {
    try {
      return await api.get(`/front-desk/folios/booking/${bookingId}`);
    } catch (error) {
      console.error("Error fetching folio:", error);
      throw error;
    }
  },
  getById: async (folioId: string) => {
    try {
      return await api.get(`/front-desk/folios/${folioId}`);
    } catch (error) {
      console.error("Error fetching folio:", error);
      throw error;
    }
  },
  getEntries: async (folioId: string, params?: { category?: string; from_date?: string; to_date?: string }) => {
    try {
      return await api.get(`/front-desk/folios/${folioId}/entries`, params);
    } catch (error) {
      console.error("Error fetching folio entries:", error);
      throw error;
    }
  },
  getSummary: async (folioId: string, params?: { from_date?: string; to_date?: string }) => {
    try {
      return await api.get(`/front-desk/folios/${folioId}/summary`, params);
    } catch (error) {
      console.error("Error fetching folio summary:", error);
      throw error;
    }
  },
  getCategoryTotals: async (folioId: string, params?: { from_date?: string; to_date?: string }) => {
    try {
      return await api.get(`/front-desk/folios/${folioId}/category-totals`, params);
    } catch (error) {
      console.error("Error fetching category totals:", error);
      throw error;
    }
  },
  postCharge: async (folioId: string, data: any) => {
    try {
      return await api.post(`/front-desk/folios/${folioId}/entries`, data);
    } catch (error) {
      console.error("Error posting charge:", error);
      throw error;
    }
  },
  postBatchCharges: async (folioId: string, entries: any[]) => {
    try {
      return await api.post(`/front-desk/folios/${folioId}/entries/batch`, { entries });
    } catch (error) {
      console.error("Error posting batch charges:", error);
      throw error;
    }
  },
  deleteEntry: async (folioId: string, entryId: string) => {
    try {
      return await api.delete(`/front-desk/folios/${folioId}/entries/${entryId}`);
    } catch (error) {
      console.error("Error deleting folio entry:", error);
      throw error;
    }
  },
  postPayment: async (folioId: string, data: any) => {
    try {
      return await api.post(`/front-desk/folios/${folioId}/payments`, data);
    } catch (error) {
      console.error("Error recording payment:", error);
      throw error;
    }
  },
  getReceipt: async (
    folioId: string,
    params?: { groupBookingIds?: string[]; from_date?: string; to_date?: string },
    customer?: {
      customerName?: string;
      companyName?: string;
      phone?: string;
      address?: string;
      email?: string;
      tinVatNumber?: string;
    },
  ) => {
    const token = localStorage.getItem("admin_token");
    const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1";
    const url = `${baseUrl}/front-desk/folios/${folioId}/receipt`;
    let response: Response;
    if (customer) {
      response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          groupBookingIds: params?.groupBookingIds,
          from_date: params?.from_date,
          to_date: params?.to_date,
          customer,
        }),
      });
    } else {
      const qs: string[] = [];
      if (params?.groupBookingIds && params.groupBookingIds.length > 0) {
        params.groupBookingIds.forEach(id => qs.push(`groupBookingIds=${encodeURIComponent(id)}`));
      }
      if (params?.from_date) qs.push(`from_date=${encodeURIComponent(params.from_date)}`);
      if (params?.to_date) qs.push(`to_date=${encodeURIComponent(params.to_date)}`);
      const getUrl = qs.length > 0 ? `${url}?${qs.join("&")}` : url;
      response = await fetch(getUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
    }
    if (!response.ok) throw new Error("Failed to fetch folio receipt");
    return response.blob();
  },
};

export const adminAuditService = {
  getLogs: async (filters: {
    entityType?: string;
    entityId?: string;
    userId?: string;
    action?: string;
    fromDate?: string;
    toDate?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    return await api.get(`/audit/logs?${params.toString()}`);
  },
  getEntityTrail: async (entityType: string, entityId: string) => {
    return await api.get(`/audit/entity/${entityType}/${entityId}`);
  },
};

export const reservationPrintService = {
  getConfirmationPdf: async (bookingId: string): Promise<Blob> => {
    return await api.getBlob(`/reservations/confirmation/${bookingId}`);
  },
  getRegistrationPdf: async (bookingId: string): Promise<Blob> => {
    return await api.getBlob(`/reservations/registration/${bookingId}`);
  },
  getArrivalListPdf: async (date: string): Promise<Blob> => {
    return await api.getBlob("/reservations/arrivals", { date });
  },
  getDepartureListPdf: async (date: string): Promise<Blob> => {
    return await api.getBlob("/reservations/departures", { date });
  },
  getOccupancyListPdf: async (date: string): Promise<Blob> => {
    return await api.getBlob("/reservations/occupancy", { date });
  },
  getAvailabilityPdf: async (date: string): Promise<Blob> => {
    return await api.getBlob("/reservations/availability", { date });
  },
  getReservationListPdf: async (dateFrom: string, dateTo: string, status?: string): Promise<Blob> => {
    const params: any = { from: dateFrom, to: dateTo };
    if (status) params.status = status;
    return await api.getBlob("/reservations/list", params);
  },
  downloadPdf: (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  },
  /**
   * Open a generated PDF in a new tab instead of saving it to disk.
   *
   * The window is opened *synchronously* here, before the fetch resolves,
   * because popup blockers require a transient user gesture. Calling
   * window.open() after an await is the classic case browsers get wrong — it
   * either gets blocked silently or the browser falls back to downloading.
   * Opening first and filling afterwards sidesteps the heuristic entirely.
   *
   * A named target is used rather than "_blank" so that printing several
   * documents in a row reuses one tab instead of stacking a new one each time.
   *
   * Known limitation: Safari does not reliably hand blob: URLs to its PDF
   * viewer and may download instead. Chrome/Edge/Firefox open the viewer.
   *
   * @returns false when the browser blocked the window, so the caller can
   * surface it. The service stays free of any UI dependency.
   */
  openPdf: (blob: Blob, title: string): boolean => {
    const win = window.open("", "resort-pdf-viewer");
    if (!win) return false;

    try {
      win.opener = null;
      win.document.title = title;
      win.document.write(
        `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>` +
          `<style>body{margin:0;background:#f5f5f5;display:flex;align-items:center;justify-content:center;` +
          `height:100vh;font:14px system-ui,sans-serif;color:#666}</style></head>` +
          `<body><p>Loading document…</p></body></html>`,
      );
      win.document.close();
    } catch {
      // Cross-origin or already-navigated window — fall through to the URL.
    }

    const url = window.URL.createObjectURL(blob);
    win.location.href = url;
    win.onbeforeunload = () => window.URL.revokeObjectURL(url);
    return true;
  },
};

export default adminServices;
